import { usePathname, useRouter } from '@/lib/navigation';
import { useTranslations } from '@/i18n';
import { useTrip } from '@/hooks/trips/useTrip';
import { useLocation } from 'react-router-dom';

export default function Breadcrumbs() {
  const pathname = usePathname();
  const router = useRouter();
  const location = useLocation();
  const t = useTranslations();

  // Extract trip ID for any trip-related page
  const pathParts = pathname.split('/');
  const tripId =
    pathname.startsWith('/trips/') && pathParts[2] && pathParts[2] !== 'new' ? pathParts[2] : '';

  // Always call the hook, it will be disabled if tripId is empty
  const { data: trip } = useTrip(tripId);
  const hasCities = (trip?.cities?.length ?? 0) > 0;

  // Check if we came from approvals page
  const locationState = location.state as { from?: string } | null;
  const cameFromApprovals = locationState?.from === '/approvals';
  const tripsListPath = cameFromApprovals ? '/approvals' : '/';
  const tripsListLabel = cameFromApprovals ? t('navigation.myApprovals') : t('trips.title');

  // Build breadcrumb trail based on current path
  if (pathname.includes('/cities')) {
    // Cities page breadcrumb
    const cityTripId = pathname.split('/')[2]; // Extract trip ID from path
    return (
      <div className="flex items-center gap-2 text-base">
        <span
          className="cursor-pointer text-[#666666] transition-colors hover:text-[#292929]"
          onClick={() => router.push(tripsListPath)}
        >
          {tripsListLabel}
        </span>
        <span className="text-[#666666]">/</span>
        <span
          className="cursor-pointer text-[#666666] transition-colors hover:text-[#292929]"
          onClick={() => router.push(`/trips/${cityTripId}`, { state: locationState })}
        >
          {hasCities ? t('tripForm.pageTitleWithCities') : t('tripForm.pageTitle')}
        </span>
        <span className="text-[#666666]">/</span>
        <span className="font-medium text-[#292929]">{t('cityForm.breadcrumb')}</span>
      </div>
    );
  } else if (pathname === '/trips' || pathname.startsWith('/trips/')) {
    // Trip pages breadcrumb
    return (
      <div className="flex items-center gap-2 text-base">
        <span
          className="cursor-pointer text-[#666666] transition-colors hover:text-[#292929]"
          onClick={() => router.push(tripsListPath)}
        >
          {tripsListLabel}
        </span>
        <span className="text-[#666666]">/</span>
        <span className="font-medium text-[#292929]">
          {hasCities ? t('tripForm.pageTitleWithCities') : t('tripForm.pageTitle')}
        </span>
      </div>
    );
  }

  // Default single breadcrumb for other pages
  const getBreadcrumbText = () => {
    switch (pathname) {
      case '/':
        return t('trips.title');
      case '/approvals':
        return t('navigation.myApprovals');
      case '/reports':
        return t('navigation.reports');
      default:
        return t('trips.title');
    }
  };

  return (
    <div className="text-base leading-normal font-medium text-[#292929]">{getBreadcrumbText()}</div>
  );
}
